import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
	ElementRef,
	AfterViewInit,
} from '@angular/core';
import { waitForVideoPreviewReady } from '../../utils/wait-for-video-preview-ready.util';
import { CameraService } from '../../services/camera.service';
import { CameraPermissionMonitorService } from '../../services/camera-permission-monitor.service';
import { CloudLogsService, LogType } from '../../services/cloud-logs.service';
import { ProcessLog } from 'src/app/user-identification/models/user-identification';
import { debounceTime, fromEvent, Subject, takeUntil } from 'rxjs';
import { ConfigService } from '../../services/config/config.service';
import { StreamingManagementService } from '../../services/streaming-management.service';
import { NgClass, NgStyle } from '@angular/common';
import { ProcessTabGuardService } from 'src/app/core/services/process-tab-guard.service';
import { CameraFocusService } from '../../services/camera-focus.service';
import {
	DEFAULT_VIDEO_OBJECT_POSITION,
	mapViewportPointToSensorNormalized,
	type VideoObjectPosition,
} from '../../services/video-cover-frame.util';

@Component({
	selector: 'app-video',
	templateUrl: './video.component.html',
	styleUrls: ['./video.component.scss'],
	imports: [NgClass, NgStyle],
	host: {
		'[class.fill-parent]': 'fillParent',
	},
})
export class VideoComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('videoElement', { static: false }) videoElementRef: ElementRef<HTMLVideoElement>;
	@ViewChild('canvasElement', { static: false }) canvasElementRef: ElementRef<HTMLCanvasElement>;
	@Input() debugMode: boolean;
	@Input() animationFrame: number;
	@Input() canvasStyle: any;
	@Input({ required: true }) constraints: MediaStreamConstraints;
	@Input() forceFlipHorizontal: boolean = false;
	@Input() zoomFactor: number = 1;
	@Input() zoomOffsetX: number = 0;
	@Input() zoomOffsetY: number = 0;
	/** Origen del zoom CSS (p. ej. `center center` en POL). */
	@Input() zoomTransformOrigin: string = '0 0';
	/** Solo POL (`tec-scan-face`): plantilla con `.webcam-wrapper` + zoom antes del clip-path de Infinity. */
	@Input() fillParent: boolean = false;
	/**
	 * Si true, al montar no reutiliza un MediaStream heredado de otra operación si está muerto
	 * (inactivo o track ended). Transiciones OCR↔POL, solo POL, documentos, etc.
	 */
	@Input() discardStaleInheritedStream: boolean = false;

	/**
	 * Si true, siempre pide un `getUserMedia` nuevo con las `constraints` actuales
	 * (p. ej. barcode 4K). No reutiliza `cameraService.stream` aunque esté vivo o haya streaming.
	 * Tras obtener el stream nuevo, detiene tracks del stream anterior y sincroniza WebRTC si aplica.
	 */
	@Input() forceFreshGetUserMedia: boolean = false;

	/**
	 * Solo para POL: en la ruta `getUserMedia`, emite `cameraStarted` después de asignar `srcObject` al vídeo,
	 * evitando que el padre lea `videoWidth` 0 en el primer tick. El resto de pantallas deben dejarlo en false.
	 */
	@Input() polDeferCameraStartedEmit: boolean = false;
	/** Habilita autofocus continuo al iniciar o cambiar cámara. */
	@Input() enableAutoFocus: boolean = true;
	/** Habilita tap/click para enfoque puntual sobre el preview. */
	@Input() enableTapToFocus: boolean = false;
	/** Recorte cover del preview para mapear POI al sensor (`center` por defecto, NIF-352). */
	@Input() focusObjectPosition: VideoObjectPosition = DEFAULT_VIDEO_OBJECT_POSITION;
	@Output() cameraError = new EventEmitter<any>();
	@Output() cameraStarted = new EventEmitter<{
		stream: MediaStream;
		video: HTMLVideoElement;
		canvas: HTMLCanvasElement;
		ctx: CanvasRenderingContext2D;
	}>();
	@Output() cameraChanged = new EventEmitter<void>();
	/** Inicio de rotación manual de cámara (preview vacío hasta nuevo stream). NIF-375. */
	@Output() cameraSwitchStarted = new EventEmitter<void>();
	/** Fin de rotación: preview con frame o timeout de `waitForVideoPreviewReady`. NIF-375. */
	@Output() cameraSwitchCompleted = new EventEmitter<{ video: HTMLVideoElement | null }>();
	/** El usuario revocó permisos de cámara con el stream ya activo (p. ej. candado del navegador). */
	@Output() public cameraPermissionRevoked = new EventEmitter<void>();

	public video!: HTMLVideoElement;
	public canvas!: HTMLCanvasElement;
	public ctx!: CanvasRenderingContext2D;
	public stream: MediaStream;
	public flipHorizontal: boolean = false;
	private destroy$: Subject<void> = new Subject<void>();
	/** Evita callbacks tardíos (getUserMedia / play) tras destruir el componente. */
	private isDestroyed = false;
	private allCameras: Array<MediaDeviceInfo> = [];
	private isChangingCamera: boolean = false;
	private hasCameraStartedBeenEmitted: boolean = false;
	private getCameraStreamAbortController: AbortController | null = null;
	private permissionWatchCleanup: (() => void) | null = null;
	/** NIF-1033: poll ligero; Cloud Log solo si cambia hasVideoFrame/dims. */
	private cameraFramePollId: ReturnType<typeof setInterval> | null = null;
	private lastLoggedFrameState: { hasVideoFrame: boolean; videoWidth: number; videoHeight: number } | null = null;
	private trackEndedCleanups: Array<() => void> = [];
	private trackEndedLogged = false;
	private trackMuteLogged = false;

	get wrapperZoomStyle(): Record<string, string> {
		return this.buildZoomStyle();
	}

	private buildZoomStyle(): Record<string, string> {
		if (this.zoomFactor === 1 && !this.zoomOffsetX && !this.zoomOffsetY) {
			return { transform: 'none', 'transform-origin': this.zoomTransformOrigin };
		}
		return {
			transform: `scale(${this.zoomFactor}) translateX(${this.zoomOffsetX}px) translateY(${this.zoomOffsetY}px)`,
			'transform-origin': this.zoomTransformOrigin,
		};
	}

	private buildTrackDiagnostics(stream: MediaStream | null): {
		deviceId: string | undefined;
		label: string | undefined;
		settings: MediaTrackSettings | null;
		capabilities: MediaTrackCapabilities | null;
	} {
		const videoTrack = stream?.getVideoTracks?.()[0];
		return {
			deviceId: videoTrack?.getSettings?.()?.deviceId,
			label: videoTrack?.label,
			settings: videoTrack?.getSettings?.() ?? null,
			capabilities: videoTrack?.getCapabilities?.() ?? null,
		};
	}

	constructor(
		private cameraService: CameraService,
		private cloudLogsService: CloudLogsService,
		private configService: ConfigService,
		private streamingManagementService: StreamingManagementService,
		private processTabGuardService: ProcessTabGuardService,
		private cameraFocusService: CameraFocusService,
		private cameraPermissionMonitorService: CameraPermissionMonitorService,
	) {
		// Escuchar cambios de cámara vía botón
		this.cameraService.changeCamera$.pipe(takeUntil(this.destroy$)).subscribe(async (deviceId) => {
			await this.changeCamera(deviceId);
		});

		// Escuchar cambios directos en el stream
		this.cameraService.stream$.pipe(takeUntil(this.destroy$)).subscribe((newStream) => {
			if (newStream && this.stream !== newStream && this.video) {
				this.updateVideoElement(newStream);
			}
		});
	}

	private async updateVideoElement(newStream: MediaStream): Promise<void> {
		try {
			this.stream = newStream;
			this.beginPermissionWatch(newStream);

			// Mutear el video element
			this.video.muted = true;

			// Actualizar video element
			this.video.srcObject = newStream;

			// Forzar carga y reproducción
			await new Promise((resolve) => {
				this.video.onloadedmetadata = async () => {
					try {
						await this.video.play();
						resolve(true);
					} catch (playError) {
						resolve(false);
					}
				};
				this.video.load();
			});

			// Actualizar flipHorizontal
			this.updateFlipHorizontal();
			this.applyContinuousAutofocus(newStream);
		} catch (error) {
			this.cloudLogsService.sendCloudLogs(
				{ updateVideoElement: error?.toString() },
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
		}
	}

	/**
	 * Helper para verificar si se debe mantener el audio
	 */
	private shouldKeepAudio(): boolean {
		try {
			// Usar getCurrentConfig() para acceder al valor actual sincronamente
			const config = this.configService.getCurrentConfig();
			const shouldKeep = config?.required_recording || false;
			return shouldKeep;
		} catch (error) {
			return false;
		}
	}

	ngOnInit(): void {
		fromEvent(navigator.mediaDevices as any, 'devicechange')
			.pipe(debounceTime(300), takeUntil(this.destroy$))
			.subscribe(() => this.cameraService.getAllCameras());

		this.cameraService.allCameras$.pipe(takeUntil(this.destroy$)).subscribe((cameras: MediaDeviceInfo[]) => {
			this.allCameras = cameras ?? [];
			this.updateFlipHorizontal();
		});
	}

	ngAfterViewInit(): void {
		// Obtener referencias del DOM usando ViewChild
		this.video = this.videoElementRef.nativeElement;
		this.canvas = this.canvasElementRef.nativeElement;
		this.ctx = this.canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;

		// Añadir pequeño delay antes de iniciar la cámara
		setTimeout(() => {
			if (this.isDestroyed) return;
			if (this.discardStaleInheritedStream) {
				const stale = this.cameraService.stream;
				if (this.cameraService.isStaleInheritedStream(stale)) {
					this.cameraService.stream = null;
				}
			}

			// Barcode / alta resolución: no heredar stream OCR (~720p) ni con streaming activo.
			if (this.forceFreshGetUserMedia) {
				this.startCamera();
				return;
			}

			const existingStream = this.cameraService.stream;
			const isStreamingActive = this.streamingManagementService.isStreamingActive();
			const isStreamingRetry = this.streamingManagementService.isStreamingRetryInProgress();

			// RUTA 1: Stream disponible y reutilizable (track vivo)
			if (existingStream && !this.cameraService.isStaleInheritedStream(existingStream)) {
				this.useStream(existingStream);
				return;
			}

			// RUTA 2: Streaming activo (transición entre operaciones) pero stream aún no listo
			// Esto ocurre durante changeOperationStreamId - debemos solicitar la nueva cámara Y esperar
			if (isStreamingActive || isStreamingRetry) {
				this.startCamera();
				return;
			}

			this.startCamera();
		}, 150);
	}

	private useStream(stream: MediaStream): void {
		this.stream = stream;
		this.video.muted = true;
		this.video.srcObject = stream;

		this.video.load();
		this.attemptPlay();
		this.beginPermissionWatch(stream);
		this.attachTrackTelemetry(stream);
		this.startCameraFramePoll();
		if (this.streamingManagementService.isStreamingActive()) {
			void this.syncStreamingTracks(stream, 'useStream');
		}

		this.cameraStarted.emit({
			stream: stream,
			video: this.video,
			canvas: this.canvas,
			ctx: this.ctx,
		});
		this.hasCameraStartedBeenEmitted = true;
		this.updateFlipHorizontal();
		this.applyContinuousAutofocus(stream);
		// NIF-1033: reutilizar stream → un solo log (sin cameraRequestStart).
		this.logCameraRequestOk(stream, null, 'useStream');
	}

	private startCamera(): void {
		if (this.shouldBlockCameraStart('startCamera')) {
			return;
		}
		if (this.discardStaleInheritedStream) {
			const svc = this.cameraService.stream;
			if (this.cameraService.isStaleInheritedStream(svc)) {
				this.cameraService.stream = null;
			}
		}

		const previousStream = this.cameraService.stream;
		const existingStream = previousStream;
		const isStreamingActive = this.streamingManagementService.isStreamingActive();

		if (
			!this.forceFreshGetUserMedia &&
			isStreamingActive &&
			existingStream &&
			!this.cameraService.isStaleInheritedStream(existingStream)
		) {
			this.useStream(existingStream);
			return;
		}

		// Limpiar cualquier stream anterior antes de solicitar uno nuevo
		if (this.stream) this.stopCamera();
		const requestedConstraints: MediaStreamConstraints = {
			...this.constraints,
			audio: this.shouldKeepAudio(),
		};
		this.cloudLogsService.sendCloudLogs(
			{
				event: 'cameraRequestStart',
				constraintsRequested: requestedConstraints,
			},
			ProcessLog.PRELOAD,
			LogType.Info,
		);
		navigator.mediaDevices
			.getUserMedia(requestedConstraints)
			.then(async (stream: MediaStream) => {
				if (this.isDestroyed || !this.video) {
					stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
					return;
				}
				this.cameraService.getAllCameras();
				this.stream = stream;
				this.cameraService.stream = stream;
				// Verificar si required_recording está habilitado
				const keepAudio = this.shouldKeepAudio();

				// Solo detener tracks de audio si NO se requiere grabación
				if (!keepAudio) {
					stream.getAudioTracks().forEach((track: MediaStreamTrack) => track.stop());
				}

				// Limpiar el video element antes de asignar el nuevo stream
				this.video.srcObject = null;
				this.video.load();

				const applyStreamToVideoAndPlay = (): void => {
					if (!this.video) return;

					// Mutear el video element para que no reproduzca audio localmente
					// pero los tracks de audio sigan disponibles para el streaming
					this.video.muted = true;

					// Asignar el stream al video element
					this.video.srcObject = stream;

					// Forzar la carga inmediata del recurso
					this.video.load();

					// Intentar reproducir con sistema de reintentos
					this.attemptPlay();
				};

				if (this.polDeferCameraStartedEmit) {
					// POL: notificar al padre solo cuando el elemento ya tiene el MediaStream asignado.
					setTimeout(() => {
						if (this.isDestroyed || !this.video) return;
						applyStreamToVideoAndPlay();
						this.cameraStarted.emit({
							stream: stream,
							video: this.video,
							canvas: this.canvas,
							ctx: this.ctx,
						});
						this.hasCameraStartedBeenEmitted = true;
						this.logCameraRequestOk(stream, requestedConstraints, 'getUserMedia');
					}, 0);
				} else {
					// Pequeña pausa para asegurar que el video element está completamente limpio
					setTimeout(() => {
						if (this.isDestroyed) return;
						applyStreamToVideoAndPlay();
					});

					this.cameraStarted.emit({ stream: stream, video: this.video, canvas: this.canvas, ctx: this.ctx });
					this.hasCameraStartedBeenEmitted = true;
					this.logCameraRequestOk(stream, requestedConstraints, 'getUserMedia');
				}
				this.beginPermissionWatch(stream);
				this.attachTrackTelemetry(stream);
				this.startCameraFramePoll();
				if (isStreamingActive) {
					await this.syncStreamingTracks(stream, 'startCamera');
				}
				this.stopReplacedStreamTracks(previousStream, stream);
				this.applyContinuousAutofocus(stream);
			})
			.catch((err) => {
				this.cameraError.emit(err);
				this.cloudLogsService.sendCloudLogs(
					{
						event: 'cameraRequestError',
						name: err?.name,
						message: err?.message ?? err?.toString?.(),
						constraintsRequested: requestedConstraints,
					},
					ProcessLog.PRELOAD,
					LogType.Error,
				);
			});
	}

	/** NIF-1033: snapshot barato (sin sample de píxeles). */
	private buildVideoFrameSnapshot(): {
		hasVideoFrame: boolean;
		videoWidth: number;
		videoHeight: number;
		readyState: number;
		paused: boolean;
		muted: boolean;
	} {
		const videoWidth = this.video?.videoWidth ?? 0;
		const videoHeight = this.video?.videoHeight ?? 0;
		return {
			hasVideoFrame: videoWidth > 0,
			videoWidth,
			videoHeight,
			readyState: this.video?.readyState ?? 0,
			paused: this.video?.paused ?? true,
			muted: this.video?.muted ?? true,
		};
	}

	private buildTrackSnapshots(stream: MediaStream): Array<{
		kind: string;
		readyState: MediaStreamTrackState;
		muted: boolean;
		enabled: boolean;
	}> {
		return stream.getTracks().map((track) => ({
			kind: track.kind,
			readyState: track.readyState,
			muted: track.muted,
			enabled: track.enabled,
		}));
	}

	private logCameraRequestOk(
		stream: MediaStream,
		constraintsRequested: MediaStreamConstraints | null,
		source: 'getUserMedia' | 'useStream',
	): void {
		if (this.isDestroyed) return;
		const videoTrack = stream.getVideoTracks()[0];
		const applied = videoTrack?.getSettings?.() ?? null;
		const frame = this.buildVideoFrameSnapshot();
		this.lastLoggedFrameState = {
			hasVideoFrame: frame.hasVideoFrame,
			videoWidth: frame.videoWidth,
			videoHeight: frame.videoHeight,
		};
		this.cloudLogsService.sendCloudLogs(
			{
				event: 'cameraRequestOk',
				source,
				constraintsRequested,
				constraintsApplied: applied
					? { facingMode: applied.facingMode, width: applied.width, height: applied.height, deviceId: applied.deviceId }
					: null,
				...frame,
				tracks: this.buildTrackSnapshots(stream),
			},
			ProcessLog.PRELOAD,
			LogType.Info,
		);
	}

	private attachTrackTelemetry(stream: MediaStream): void {
		this.clearTrackTelemetry();
		this.trackEndedLogged = false;
		this.trackMuteLogged = false;
		stream.getTracks().forEach((track) => {
			const onEnded = (): void => {
				if (this.isDestroyed || this.trackEndedLogged) return;
				this.trackEndedLogged = true;
				this.cloudLogsService.sendCloudLogs(
					{
						event: 'cameraTrackEnded',
						kind: track.kind,
						label: track.label,
					},
					ProcessLog.IN_PROCESS,
					LogType.Warning,
				);
			};
			const onMute = (): void => {
				if (this.isDestroyed || this.trackMuteLogged || track.kind !== 'video') return;
				this.trackMuteLogged = true;
				this.cloudLogsService.sendCloudLogs(
					{
						event: 'cameraTrackMute',
						kind: track.kind,
						readyState: track.readyState,
					},
					ProcessLog.IN_PROCESS,
					LogType.Warning,
				);
			};
			track.addEventListener('ended', onEnded);
			track.addEventListener('mute', onMute);
			this.trackEndedCleanups.push(() => {
				track.removeEventListener('ended', onEnded);
				track.removeEventListener('mute', onMute);
			});
		});
	}

	private clearTrackTelemetry(): void {
		this.trackEndedCleanups.forEach((cleanup) => cleanup());
		this.trackEndedCleanups = [];
	}

	private startCameraFramePoll(): void {
		this.stopCameraFramePoll();
		this.cameraFramePollId = setInterval(() => {
			if (this.isDestroyed || !this.video) {
				this.stopCameraFramePoll();
				return;
			}
			const snap = this.buildVideoFrameSnapshot();
			const next = {
				hasVideoFrame: snap.hasVideoFrame,
				videoWidth: snap.videoWidth,
				videoHeight: snap.videoHeight,
			};
			const prev = this.lastLoggedFrameState;
			if (
				prev &&
				prev.hasVideoFrame === next.hasVideoFrame &&
				prev.videoWidth === next.videoWidth &&
				prev.videoHeight === next.videoHeight
			) {
				return;
			}
			this.lastLoggedFrameState = next;
			this.cloudLogsService.sendCloudLogs(
				{
					event: 'cameraTick',
					...next,
				},
				ProcessLog.IN_PROCESS,
				LogType.Info,
			);
		}, 5000);
	}

	private stopCameraFramePoll(): void {
		if (this.cameraFramePollId != null) {
			clearInterval(this.cameraFramePollId);
			this.cameraFramePollId = null;
		}
	}

	/** Libera tracks del stream OCR/previo tras un getUserMedia fresco (p. ej. barcode 4K). */
	private stopReplacedStreamTracks(previous: MediaStream | null, next: MediaStream): void {
		if (!previous || previous === next) {
			return;
		}
		const nextTrackIds = new Set(next.getTracks().map((t) => t.id));
		previous.getTracks().forEach((track) => {
			if (!nextTrackIds.has(track.id)) {
				track.stop();
			}
		});
	}

	private shouldBlockCameraStart(source: 'startCamera' | 'getCameraStream'): boolean {
		const processUuid = this.cloudLogsService.getProcessUUID()?.trim();
		if (!processUuid) {
			return false;
		}

		if (!this.processTabGuardService.isBlockedByAnotherTab(processUuid)) {
			return false;
		}

		const error = {
			name: 'ProcessAlreadyActiveError',
			message: 'El proceso ya está activo en otra pestaña de este navegador.',
		};
		this.cameraError.emit(error);
		this.cloudLogsService.sendCloudLogs(
			{
				processTabGuard: {
					source,
					blocked: true,
					processUuid,
				},
			},
			ProcessLog.IN_PROCESS,
			LogType.Warning,
		);
		return true;
	}

	private async syncStreamingTracks(stream: MediaStream, source: 'startCamera' | 'useStream'): Promise<void> {
		try {
			const updated = await this.streamingManagementService.updateStream(stream);
			if (!updated) {
				this.cloudLogsService.sendCloudLogs(
					{
						ocrStreamingTrackSync: {
							source,
							success: false,
							...this.buildTrackDiagnostics(stream),
						},
					},
					ProcessLog.IN_PROCESS,
					LogType.Warning,
				);
			}
		} catch (error: any) {
			this.cloudLogsService.sendCloudLogs(
				{
					ocrStreamingTrackSync: {
						source,
						success: false,
						errorName: error?.name,
						errorMessage: error?.message,
						errorText: error?.toString?.(),
					},
				},
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
		}
	}

	private attemptPlay(attempt: number = 1): void {
		if (this.isDestroyed || !this.video) return;
		this.video.play().catch(() => {
			if (this.isDestroyed || !this.video) return;
			if (attempt < 3) {
				setTimeout(() => {
					this.attemptPlay(attempt + 1);
				}, 100);
			} else {
				this.cloudLogsService.sendCloudLogs(
					{ videoPlay: 'Failed after three attempts' },
					ProcessLog.IN_PROCESS,
					LogType.Error,
				);
			}
		});
	}

	private beginPermissionWatch(stream: MediaStream): void {
		this.clearPermissionWatch();
		this.permissionWatchCleanup = this.cameraPermissionMonitorService.watchStream(stream, () => {
			if (this.isDestroyed) return;
			if (this.video) {
				this.video.srcObject = null;
				this.video.load();
			}
			this.cameraPermissionRevoked.emit();
		});
	}

	private clearPermissionWatch(): void {
		if (this.permissionWatchCleanup) {
			this.permissionWatchCleanup();
			this.permissionWatchCleanup = null;
		}
	}

	private stopCamera() {
		this.stopCameraFramePoll();
		this.clearTrackTelemetry();
		this.clearPermissionWatch();
		if (this.stream && typeof this.stream.getTracks === 'function') {
			const tracks = this.stream.getTracks();
			if (tracks && tracks.length > 0) {
				tracks.forEach((track: MediaStreamTrack) => {
					if (track && typeof track.stop === 'function') {
						track.stop();
					}
				});
			}
		}
		if (this.video) {
			this.video.srcObject = null;
		}
	}

	private async changeCamera(targetDeviceId?: string): Promise<void> {
		if (this.isChangingCamera) return;
		this.isChangingCamera = true;
		this.cameraSwitchStarted.emit();
		const fromDeviceId = this.cameraService.getCurrentDeviceId();

		try {
			// Obtener nueva cámara primero (sin esperar a limpiar la anterior)
			const currentDeviceId = this.cameraService.getCurrentDeviceId();
			const currentIndex = this.allCameras.findIndex((md: MediaDeviceInfo) => md.deviceId === currentDeviceId);

			if (!this.allCameras || this.allCameras.length === 0) {
				throw new Error('No cameras available');
			}

			let newCamera: MediaDeviceInfo | undefined;
			if (targetDeviceId) {
				if (targetDeviceId === currentDeviceId) {
					this.emitCameraSwitchCompleted();
					return;
				}
				newCamera = this.allCameras.find((md: MediaDeviceInfo) => md.deviceId === targetDeviceId);
				if (!newCamera) {
					this.cloudLogsService.sendCloudLogs(
						{ changeCamera: `Target deviceId not found: ${targetDeviceId}` },
						ProcessLog.IN_PROCESS,
						LogType.Warning,
					);
					newCamera =
						currentIndex > -1 && this.allCameras[currentIndex + 1]
							? this.allCameras[currentIndex + 1]
							: this.allCameras[0];
				}
			} else {
				newCamera =
					currentIndex > -1 && this.allCameras[currentIndex + 1]
						? this.allCameras[currentIndex + 1]
						: this.allCameras[0];
			}

			if (!newCamera || !newCamera.deviceId) throw new Error('No valid deviceId found for the camera.');

			const found = this.allCameras.some((cam) => cam.deviceId === newCamera.deviceId);
			if (!found) throw new Error(`The deviceId ${newCamera.deviceId} is not in the list of devices.`);

			// NUEVA ESTRATEGIA: Detener el stream local PERO mantener el WebRTC activo
			const wasStreamingActive = this.streamingManagementService.isStreamingActive();

			// Paso 1: Detener los tracks del stream ACTUAL para liberar la cámara
			// (limpiar watch antes de stop: track.stop() dispara `ended` y no debe parecer revocación)
			this.clearPermissionWatch();
			if (this.stream) {
				const oldTracks = this.stream.getTracks();
				oldTracks.forEach((track) => {
					track.stop();
				});
			}

			// Paso 2: Obtener el nuevo stream (ahora la cámara está libre)
			const stream = await this.getCameraStream(newCamera);

			if (!stream || !stream.active) throw new Error('Stream obtained but not active');

			// Verificar si required_recording está habilitado
			const keepAudio = this.shouldKeepAudio();

			// Solo limpiar audio si NO se requiere grabación
			if (!keepAudio && stream && typeof stream.getAudioTracks === 'function') {
				const audioTracks = stream.getAudioTracks();
				if (audioTracks && audioTracks.length > 0) {
					audioTracks.forEach((track: MediaStreamTrack) => {
						if (track && typeof track.stop === 'function') track.stop();
						if (typeof stream.removeTrack === 'function') stream.removeTrack(track);
					});
				}
			}

			// Verificar que tenemos al menos un track de video
			const hasVideoTracks =
				stream && typeof stream.getVideoTracks === 'function' && stream.getVideoTracks().length > 0;

			if (!hasVideoTracks) throw new Error('The stream does not contain valid video tracks.');

			// Paso 3: Si hay streaming activo, reemplazar los tracks INMEDIATAMENTE en WebRTC
			// Esto minimiza el gap en el streaming (solo el tiempo entre stop y replaceTrack)
			if (wasStreamingActive) {
				const replaceSuccess = await this.streamingManagementService.updateStream(stream);
			}

			// Paso 4: Actualizar el video element con el nuevo stream
			// Asignar el nuevo stream a las referencias
			this.cameraService.stream = stream;
			this.stream = stream;

			// Actualizar video element DIRECTAMENTE
			if (this.video) {
				this.video.srcObject = stream;

				// Paso 5: Intentar reproducir
				try {
					await this.video.play();
				} catch (playError) {
					await new Promise<void>((resolve) => {
						let resolved = false;

						const cleanupListeners = () => {
							this.video.removeEventListener('canplay', onCanPlay);
							this.video.removeEventListener('loadedmetadata', onLoadedMetadata);
						};

						const playVideo = async () => {
							try {
								await this.video.play();
								resolve();
							} catch (playErr) {
								const errMsg = playErr?.toString() || 'Unknown error';
								this.cloudLogsService.sendCloudLogs(
									{ changeCameraPlayError: errMsg },
									ProcessLog.IN_PROCESS,
									LogType.Warning,
								);
								resolve();
							}
						};

						// Timeout de seguridad
						const timeoutId = setTimeout(() => {
							if (!resolved) {
								resolved = true;
								cleanupListeners();
								playVideo();
							}
						}, 3000);

						// Event listeners
						const onCanPlay = async () => {
							if (resolved) return;
							resolved = true;
							clearTimeout(timeoutId);
							cleanupListeners();
							await playVideo();
						};

						const onLoadedMetadata = async () => {
							if (resolved) return;
							resolved = true;
							clearTimeout(timeoutId);
							cleanupListeners();
							await playVideo();
						};

						this.video.addEventListener('canplay', onCanPlay);
						this.video.addEventListener('loadedmetadata', onLoadedMetadata);

						// Forzar carga
						this.video.load();
					});
				}
			} else {
				this.cloudLogsService.sendCloudLogs(
					{ warning: 'video element not found' },
					ProcessLog.IN_PROCESS,
					LogType.Warning,
				);
			}

			// Actualizar flipHorizontal
			this.updateFlipHorizontal();
			this.applyContinuousAutofocus(stream);
			this.beginPermissionWatch(stream);

			await waitForVideoPreviewReady(this.video);

			// Emitir evento (NO reiniciamos streaming porque usamos replaceTrack)
			this.cameraChanged.emit();
			this.emitCameraSwitchCompleted();
		} catch (error) {
			this.emitCameraSwitchCompleted();
			this.cloudLogsService.sendCloudLogs(
				{
					changeCamera: {
						name: error?.name,
						message: error?.message ?? error?.toString?.(),
						fromDeviceId,
					},
				},
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);

			// Intentar recuperar la cámara original si el cambio falló
			try {
				this.startCamera();
			} catch (recoveryError) {
				this.cloudLogsService.sendCloudLogs(
					{
						changeCameraRecovery: {
							name: recoveryError?.name,
							message: recoveryError?.message ?? recoveryError?.toString?.(),
						},
					},
					ProcessLog.PRELOAD,
					LogType.Error,
				);
				this.cameraError.emit(error);
			}
		} finally {
			this.isChangingCamera = false;
		}
	}

	/**
	 * Obtiene un flujo de vídeo de la cámara especificada aplicando diferentes estrategias de constraints.
	 *
	 * Intenta primero obtener el stream con constraints estrictos (deviceId exacto) y, si falla,
	 * realiza varios intentos con diferentes configuraciones, incluyendo constraints ideales y mínimos.
	 * Entre cada intento, espera un tiempo determinado según si es la carga inicial.
	 *
	 * @param newCamera Información del dispositivo de cámara a utilizar.
	 * @param skipCleanup Si es true, NO detiene los tracks anteriores (usado durante cambio de cámara con streaming activo)
	 * @returns Una promesa que se resuelve con el MediaStream de la cámara seleccionada.
	 * @throws Error si no se puede obtener un stream válido tras varios intentos.
	 */
	private emitCameraSwitchCompleted(): void {
		this.cameraSwitchCompleted.emit({ video: this.video ?? null });
	}

	private async getCameraStream(newCamera: MediaDeviceInfo, skipCleanup = false): Promise<MediaStream> {
		if (this.shouldBlockCameraStart('getCameraStream')) {
			throw new Error('Process already active in another tab');
		}

		// Cancelar cualquier solicitud anterior de getCameraStream
		if (this.getCameraStreamAbortController) {
			this.getCameraStreamAbortController.abort();
		}

		// Crear nuevo AbortController para esta solicitud
		this.getCameraStreamAbortController = new AbortController();
		const abortSignal = this.getCameraStreamAbortController.signal;

		// SOLO limpiar tracks si NO hay streaming activo
		if (!skipCleanup) {
			// Esperar a que cualquier stream anterior sea completamente liberado
			if (this.videoElementRef?.nativeElement) {
				const videoEl = this.videoElementRef.nativeElement as HTMLVideoElement;
				if (videoEl.srcObject) {
					const oldStream = videoEl.srcObject as MediaStream;

					oldStream.getTracks().forEach((track: MediaStreamTrack) => {
						track.stop();
					});
					videoEl.srcObject = null;
				}
			}

			// También detener tracks del cameraService.stream si existe
			if (this.cameraService.stream) {
				this.cameraService.stream.getTracks().forEach((track: MediaStreamTrack) => {
					track.stop();
				});
			}

			// Breve pausa para asegurar que el cleanup está completo
			await new Promise<void>((resolve) => setTimeout(resolve, 100));
		}

		const shouldIncludeAudio = this.shouldKeepAudio();

		// Usar exactamente las mismas constraints, solo cambiando el deviceId
		const videoConstraints =
			this.constraints.video === true
				? { deviceId: { exact: newCamera.deviceId } }
				: {
						...this.constraints.video,
						deviceId: { exact: newCamera.deviceId },
					};

		const constraints = {
			...this.constraints,
			audio: shouldIncludeAudio,
			video: videoConstraints,
		};
		// Verificar si fue abortado
		if (abortSignal.aborted) {
			throw new Error('getCameraStream was aborted');
		}

		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				...constraints,
				signal: abortSignal,
			} as any);

			// Marcar este AbortController como usado exitosamente
			this.getCameraStreamAbortController = null;
			return stream;
		} catch (err) {
			// Si fue abortado, no continuar
			if ((err as any)?.name === 'AbortError') {
				throw err;
			}
			this.cloudLogsService.sendCloudLogs(
				{
					getCameraStream: {
						name: err?.name,
						message: err?.message ?? err?.toString?.(),
						targetDeviceId: newCamera.deviceId,
					},
				},
				ProcessLog.IN_PROCESS,
				LogType.Error,
			);
			throw err;
		}
	}

	/**
	 * Actualiza la propiedad `flipVideo` según la etiqueta del dispositivo de cámara actual.
	 * Si la etiqueta contiene las palabras 'back', 'rear' o 'environment', se considera que la cámara es trasera
	 * y se activa el volteo de vídeo.
	 */
	private updateFlipHorizontal(): void {
		if (this.forceFlipHorizontal) {
			this.flipHorizontal = true;
			return;
		}

		const currentLabel = this.cameraService.getCurrentDeviceLabel()?.toLowerCase() || '';
		this.flipHorizontal =
			currentLabel.includes('back') ||
			currentLabel.includes('rear') ||
			currentLabel.includes('environment') ||
			currentLabel.includes('trasera');
	}

	private getActiveVideoTrack(stream: MediaStream | null | undefined): MediaStreamTrack | null {
		const track = stream?.getVideoTracks?.()?.[0];
		if (!track || track.readyState === 'ended') return null;
		return track;
	}

	private applyContinuousAutofocus(stream: MediaStream | null | undefined): void {
		if (!this.enableAutoFocus || !this.video) return;
		const track = this.getActiveVideoTrack(stream);
		if (!track) return;

		const vw = this.video.videoWidth;
		const vh = this.video.videoHeight;
		if (!vw || !vh) {
			void this.cameraFocusService.applyContinuousFocus(track);
			return;
		}

		const previewRect = this.video.getBoundingClientRect();
		const viewportAspect =
			previewRect.width > 0 && previewRect.height > 0 ? previewRect.width / previewRect.height : vw / vh;
		void this.cameraFocusService.applyInitialFocus(track, vw, vh, viewportAspect, this.focusObjectPosition);
	}

	public onVideoTap(event: MouseEvent): void {
		if (!this.enableTapToFocus || !this.video) return;

		const track = this.getActiveVideoTrack(this.stream);
		if (!track || !this.cameraFocusService.supportsTapToFocus(track)) return;

		const vw = this.video.videoWidth;
		const vh = this.video.videoHeight;
		if (!vw || !vh) return;

		const rect = this.video.getBoundingClientRect();
		if (!rect.width || !rect.height) return;

		const relativeX = (event.clientX - rect.left) / rect.width;
		const relativeY = (event.clientY - rect.top) / rect.height;
		const relX = this.flipHorizontal ? 1 - relativeX : relativeX;
		const viewportAspect = rect.width / rect.height;
		const point = mapViewportPointToSensorNormalized(
			relX,
			relativeY,
			vw,
			vh,
			viewportAspect,
			this.focusObjectPosition,
		);

		void this.cameraFocusService.triggerTapRefocus(track, point);
	}

	ngOnDestroy(): void {
		this.isDestroyed = true;
		this.stopCameraFramePoll();
		this.clearTrackTelemetry();
		this.clearPermissionWatch();
		if (this.getCameraStreamAbortController) {
			this.getCameraStreamAbortController.abort();
		}

		// NIF-963: en RETRY MEDIA_SERVER el OCR remonta; no matar tracks o WHIP queda sin vídeo live.
		const keepAliveForStreaming =
			(this.streamingManagementService?.isStreamingActive() ?? false) ||
			(this.streamingManagementService?.isStreamingRetryInProgress() ?? false);

		if (keepAliveForStreaming) {
			if (this.video) {
				this.video.srcObject = null;
			}
		} else {
			this.stopCamera();
			if (this.video) {
				this.video.srcObject = null;
			}
		}

		this.video = null;
		this.destroy$.next();
		this.destroy$.complete();
	}
}
